import { useEffect } from "react";
import { Link } from "react-router-dom";

import { Footer } from "@/components/landing/Footer";
import { trackEvent } from "@/lib/analytics";

const sections = [
  {
    number: "1.",
    title: "What Quantify Is",
    paragraphs: [
      "Quantify is an AI-assisted nutrition and fitness tracking service designed to help users:",
    ],
    items: [
      "record food and meals;",
      "estimate calories and nutritional information;",
      "record physical activity and workouts;",
      "record and monitor body weight;",
      "understand calorie and protein targets;",
      "receive nutrition-related suggestions and recommendations;",
      "view progress and trends; and",
      "receive automated coaching and other personalized information.",
    ],
    trailingParagraphs: [
      "Quantify may use artificial intelligence, automated systems, databases, and third-party services to provide these features.",
      "Quantify is designed to assist with general nutrition, fitness, and weight-management goals. It is not a replacement for a qualified doctor, registered dietitian, nutritionist, physiotherapist, psychologist, or other healthcare professional.",
    ],
  },
  {
    number: "2.",
    title: "Eligibility",
    subsections: [
      {
        heading: "2.1 Minimum Age",
        paragraphs: [
          "You must be 18 years of age or older to use Quantify.",
          "By registering, you represent and warrant that:",
        ],
        items: [
          "you are at least 18 years old;",
          "you have the legal capacity to enter into these Terms; and",
          "the information you provide to Quantify is accurate and truthful.",
        ],
        trailingParagraphs: [
          "Quantify does not knowingly provide its Service to individuals under 18.",
          "We may suspend or terminate an account if we reasonably believe that the account holder does not satisfy the minimum age requirement.",
        ],
      },
      {
        heading: "2.2 Accuracy of Information",
        paragraphs: [
          "You are responsible for providing accurate information, including where applicable:",
        ],
        items: [
          "age;",
          "sex/gender;",
          "height;",
          "weight;",
          "target weight;",
          "activity level;",
          "dietary preferences;",
          "food and exercise information; and",
          "relevant health information.",
        ],
        trailingParagraphs: [
          "Your calorie, protein, and other recommendations may be materially affected by inaccurate information.",
        ],
      },
    ],
  },
  {
    number: "3.",
    title: "Health and Medical Disclaimer",
    paragraphs: [
      "Quantify is not a medical service.",
      "The Service provides general informational and educational assistance relating to nutrition, food, physical activity, weight management, and lifestyle.",
      "Quantify does not:",
    ],
    items: [
      "diagnose diseases or medical conditions;",
      "treat diseases or medical conditions;",
      "prescribe medication;",
      "prescribe medical treatment;",
      "replace professional medical advice;",
      "guarantee any health outcome; or",
      "provide emergency medical assistance.",
    ],
    trailingParagraphs: [
      "You should consult an appropriately qualified healthcare professional before making significant changes to your diet, exercise, medication, or lifestyle, particularly if you have a medical condition or are taking medication.",
      "This is particularly important if you have, or believe you may have:",
    ],
    trailingItems: [
      "diabetes;",
      "thyroid disorders;",
      "PCOS/PCOD;",
      "cardiovascular disease;",
      "kidney or liver disease;",
      "an eating disorder;",
      "pregnancy or breastfeeding-related nutritional needs; or",
      "another medical condition that may affect nutrition, weight, or exercise.",
    ],
    closingParagraphs: [
      "Do not rely on Quantify for medical decisions.",
      "If you experience a medical emergency, contact your local emergency service or seek immediate medical attention.",
    ],
  },
  {
    number: "4.",
    title: "Calorie, Nutrition, and Activity Estimates",
    paragraphs: [
      "Quantify may provide estimates for:",
    ],
    items: [
      "calories;",
      "protein;",
      "carbohydrates;",
      "fats;",
      "fibre;",
      "calories burned through physical activity;",
      "calorie targets;",
      "maintenance calories;",
      "calorie deficits; and",
      "other nutritional or fitness metrics.",
    ],
    trailingParagraphs: [
      "These figures are estimates, not measurements.",
      "Actual nutritional values can vary substantially depending on:",
    ],
    trailingItems: [
      "ingredients;",
      "preparation method;",
      "cooking oil;",
      "portion size;",
      "restaurant or brand;",
      "recipe;",
      "serving size;",
      "individual metabolism;",
      "exercise intensity; and",
      "other factors.",
    ],
    closingParagraphs: [
      "Where you provide an incomplete description of a meal or activity, Quantify may make assumptions to generate an estimate.",
      "You acknowledge that Quantify's estimates may be inaccurate.",
      "You should not make medical or safety-critical decisions solely on the basis of Quantify's estimates.",
    ],
  },
  {
    number: "5.",
    title: "AI-Generated Information",
    paragraphs: [
      "Quantify uses artificial intelligence and automated systems to interpret user inputs and generate responses.",
      "AI-generated information may occasionally be:",
    ],
    items: [
      "inaccurate;",
      "incomplete;",
      "inconsistent;",
      "outdated;",
      "based on incorrect assumptions; or",
      "inappropriate for your particular circumstances.",
    ],
    trailingParagraphs: [
      "You should use reasonable judgment when relying on information provided by Quantify.",
      "Quantify does not guarantee that every AI-generated response will be accurate or suitable for your individual circumstances.",
    ],
  },
  {
    number: "6.",
    title: "No Guarantee of Weight Loss",
    paragraphs: [
      "Quantify does not guarantee that you will:",
    ],
    items: [
      "lose weight;",
      "reach a particular target weight;",
      "lose weight within a particular period;",
      "reduce body fat;",
      "improve a health condition;",
      "achieve a particular body composition; or",
      "achieve any other specific result.",
    ],
    trailingParagraphs: [
      "Results vary between individuals and depend on numerous factors, including nutrition, physical activity, sleep, health, medication, metabolism, adherence, and other circumstances.",
      "Any projected weight-loss rate, calorie target, or estimated timeline is an estimate only.",
    ],
  },
  {
    number: "7.",
    title: "Your Account",
    paragraphs: [
      "You may be required to provide certain information to create and use an account.",
      "You agree to:",
    ],
    items: [
      "provide accurate information;",
      "keep your information reasonably up to date;",
      "maintain the security of your account and associated credentials;",
      "not allow unauthorized persons to use your account; and",
      "notify us if you believe your account has been compromised.",
    ],
    trailingParagraphs: [
      "You are responsible for activity occurring through your account unless caused by our breach of applicable law.",
    ],
  },
  {
    number: "8.",
    title: "WhatsApp Service",
    paragraphs: [
      "Quantify may provide its Service through WhatsApp.",
      "To use Quantify through WhatsApp:",
    ],
    items: [
      "you must provide a valid WhatsApp-enabled telephone number;",
      "you must comply with WhatsApp's applicable terms and policies;",
      "you must provide any required consent to receive communications from Quantify; and",
      "you must not use the Service in a manner that violates WhatsApp's policies.",
    ],
    trailingParagraphs: [
      "WhatsApp is a third-party platform. Quantify does not control WhatsApp's availability, functionality, policies, or technical operation.",
      "Changes to WhatsApp's policies, APIs, pricing, availability, message limits, or functionality may affect Quantify.",
      "You may stop receiving certain communications by using the applicable opt-out mechanism provided by Quantify.",
    ],
  },
  {
    number: "9.",
    title: "User Communications",
    paragraphs: [
      "You may communicate with Quantify by sending information such as:",
    ],
    items: [
      "meals;",
      "food descriptions;",
      "photographs;",
      "weight;",
      "workouts;",
      "physical activity;",
      "dietary preferences;",
      "goals;",
      "questions; and",
      "other information necessary to use the Service.",
    ],
    trailingParagraphs: [
      "You represent that you have the right to provide the information you submit.",
      "You must not intentionally submit:",
    ],
    trailingItems: [
      "another person's private information without authorization;",
      "unlawful content;",
      "malicious code;",
      "content intended to disrupt the Service; or",
      "information that you are prohibited from sharing.",
    ],
  },
  {
    number: "10.",
    title: "User Content",
    paragraphs: [
      "You retain your rights in content and information that you submit to Quantify.",
      "You grant Quantify a limited, non-exclusive, worldwide, royalty-free licence to host, store, reproduce, process, and use your submitted information only to the extent reasonably necessary to provide, operate, maintain, secure, improve, and support the Service, and as otherwise described in our Privacy Policy.",
      "We do not acquire ownership of your personal food, weight, workout, or other user-generated information merely because you submit it to us.",
    ],
  },
  {
    number: "11.",
    title: "Personal Data and Privacy",
    paragraphs: [
      "Quantify's collection and processing of personal data is governed by its Privacy Policy, which forms part of these Terms.",
      "Our Privacy Policy explains:",
    ],
    items: [
      "what information we collect;",
      "why we collect it;",
      "how we use it;",
      "how we share it;",
      "how long we retain it;",
      "how we protect it;",
      "your rights and choices; and",
      "how you can contact us regarding your personal data.",
    ],
    trailingParagraphs: [
      "Quantify may process information such as weight, food intake, physical activity, dietary preferences, and other information you voluntarily provide.",
      "Some information may be sensitive or health-related. We will process such information in accordance with applicable law and our Privacy Policy.",
    ],
  },
  {
    number: "12.",
    title: "Subscription and Fees",
    paragraphs: [
      "Certain Quantify features may require a paid subscription.",
      "The applicable subscription price will be displayed to you before purchase.",
      "Unless otherwise stated, Quantify's current subscription price is:",
    ],
    emphasis: "₹249 per month",
    trailingParagraphs: [
      "We may offer:",
    ],
    trailingItems: [
      "free trials;",
      "introductory pricing;",
      "promotional pricing;",
      "discounts;",
      "limited-time offers; or",
      "other pricing arrangements.",
    ],
    closingParagraphs: [
      "The applicable price presented to you at the time of purchase will govern that purchase.",
    ],
  },
  {
    number: "13.",
    title: "Free Trials and Promotional Offers",
    paragraphs: [
      "Where Quantify offers a free or discounted trial:",
    ],
    items: [
      "the duration of the trial will be disclosed at the time of signup;",
      "eligibility may be limited;",
      "one person or payment method may be restricted to one promotional offer; and",
      "Quantify may modify or withdraw promotional offers at any time, subject to applicable law.",
    ],
    trailingParagraphs: [
      "Unless expressly stated otherwise, promotional pricing does not create a permanent entitlement to that price.",
    ],
  },
  {
    number: "14.",
    title: "Automatic Renewal",
    paragraphs: [
      "If your subscription is configured to renew automatically, your subscription will renew at the applicable renewal price unless you cancel before the renewal date.",
      "You authorize the applicable payment provider to charge the applicable subscription fee using your selected payment method.",
      "If payment fails, Quantify may:",
    ],
    items: [
      "retry the payment;",
      "suspend paid features;",
      "terminate the subscription; or",
      "require you to provide a new payment method.",
    ],
    trailingParagraphs: [
      "The specific billing and cancellation mechanism may depend on the payment provider through which you purchased the subscription.",
    ],
  },
  {
    number: "15.",
    title: "Cancellation",
    paragraphs: [
      "You may cancel your subscription in accordance with the cancellation process provided by Quantify or the applicable payment provider.",
      "Cancellation generally prevents future renewal but does not necessarily create an entitlement to a refund for a period that has already been paid for.",
    ],
  },
  {
    number: "16.",
    title: "Refunds",
    paragraphs: [
      "Refunds will be governed by Quantify's applicable refund policy and applicable consumer-protection law.",
      "Unless required otherwise by law, subscription fees are generally non-refundable after the applicable billing period has begun.",
      "Nothing in these Terms limits any consumer rights that cannot lawfully be excluded or restricted.",
      "We may provide refunds or credits at our discretion in circumstances including:",
    ],
    items: [
      "duplicate charges;",
      "technical billing errors;",
      "unauthorized charges where appropriately verified; or",
      "other circumstances determined by Quantify.",
    ],
  },
  {
    number: "17.",
    title: "Changes to Pricing",
    paragraphs: [
      "Quantify may change its pricing from time to time.",
      "If a price change affects an existing recurring subscription, we will provide appropriate notice where required by applicable law.",
      "Your continued use of the paid Service after the applicable effective date may constitute acceptance of the revised pricing, subject to applicable law.",
    ],
  },
  {
    number: "18.",
    title: "Intellectual Property",
    paragraphs: [
      "The Service and its underlying materials are owned by or licensed to Quantify and are protected by applicable intellectual-property laws.",
      "This includes, where applicable:",
    ],
    items: [
      "Quantify's name and branding;",
      "Leo and other characters;",
      "software;",
      "algorithms;",
      "databases;",
      "user interfaces;",
      "graphics;",
      "designs;",
      "text;",
      "content;",
      "workflows;",
      "product architecture;",
      "trademarks; and",
      "other proprietary materials.",
    ],
    trailingParagraphs: [
      "Except as expressly permitted by these Terms, you may not:",
    ],
    trailingItems: [
      "copy;",
      "reproduce;",
      "modify;",
      "distribute;",
      "sell;",
      "license;",
      "reverse engineer;",
      "scrape;",
      "exploit commercially; or",
      "create derivative works from",
    ],
    closingParagraphs: [
      "any part of the Service without our prior written permission.",
    ],
  },
  {
    number: "19.",
    title: "Acceptable Use",
    paragraphs: [
      "You must not use Quantify to:",
    ],
    items: [
      "violate any applicable law;",
      "impersonate another person or entity;",
      "interfere with the Service;",
      "attempt unauthorized access to systems or accounts;",
      "introduce malware or malicious code;",
      "scrape or systematically extract Service data;",
      "reverse engineer the Service except where expressly permitted by law;",
      "use the Service to develop a competing product using proprietary information;",
      "abuse, harass, or threaten Quantify personnel;",
      "submit unlawful or fraudulent information;",
      "circumvent subscription or access controls;",
      "use automated systems to overload the Service; or",
      "use the Service in a manner that may harm Quantify, its users, or third-party providers.",
    ],
  },
  {
    number: "20.",
    title: "Third-Party Services",
    paragraphs: [
      "Quantify may rely on third-party providers, including providers of:",
    ],
    items: [
      "WhatsApp messaging infrastructure;",
      "cloud hosting;",
      "artificial intelligence models;",
      "payment processing;",
      "analytics;",
      "databases;",
      "authentication;",
      "communications; and",
      "other technical services.",
    ],
    trailingParagraphs: [
      "Third-party services may have their own terms and privacy policies.",
      "Quantify is not responsible for the independent acts, omissions, availability, or policies of third-party services.",
    ],
  },
  {
    number: "21.",
    title: "Service Availability",
    paragraphs: [
      "We aim to keep Quantify available and reliable but do not guarantee uninterrupted access.",
      "The Service may become unavailable because of:",
    ],
    items: [
      "maintenance;",
      "software updates;",
      "infrastructure failures;",
      "third-party outages;",
      "WhatsApp outages;",
      "internet failures;",
      "cybersecurity incidents;",
      "excessive traffic;",
      "force majeure events; or",
      "other circumstances beyond our reasonable control.",
    ],
    trailingParagraphs: [
      "We may modify, suspend, or discontinue portions of the Service where reasonably necessary.",
    ],
  },
  {
    number: "22.",
    title: "Account Suspension and Termination",
    paragraphs: [
      "We may suspend or terminate your account if:",
    ],
    items: [
      "you materially breach these Terms;",
      "you provide fraudulent information;",
      "your use creates a security or legal risk;",
      "you abuse the Service;",
      "your payment obligations remain unpaid;",
      "required by law or a competent authority; or",
      "continued access would materially harm Quantify or other users.",
    ],
    trailingParagraphs: [
      "Where reasonably practicable, we will provide notice before termination unless immediate action is reasonably necessary.",
      "You may stop using the Service at any time.",
    ],
  },
  {
    number: "23.",
    title: "Effect of Termination",
    paragraphs: [
      "After termination:",
    ],
    items: [
      "your right to use the Service will cease;",
      "outstanding payment obligations may remain due; and",
      "provisions that by their nature should survive termination will continue to apply.",
    ],
    trailingParagraphs: [
      "Our handling of your personal information after termination will be governed by our Privacy Policy and applicable law.",
    ],
  },
  {
    number: "24.",
    title: "Limitation of Liability",
    paragraphs: [
      "To the maximum extent permitted by applicable law, Quantify and its directors, officers, employees, contractors, and service providers will not be liable for indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, the Service.",
      "This includes, where legally permissible, loss arising from:",
    ],
    items: [
      "reliance on nutritional estimates;",
      "reliance on AI-generated information;",
      "failure to achieve weight-loss goals;",
      "interruption of the Service;",
      "third-party service failures;",
      "inaccurate user-provided information; or",
      "unauthorized access caused by circumstances outside our reasonable control.",
    ],
    trailingParagraphs: [
      "Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or limited under applicable law.",
    ],
  },
  {
    number: "25.",
    title: "Maximum Liability",
    paragraphs: [
      "To the maximum extent permitted by applicable law, Quantify's aggregate liability arising out of or relating to the Service will be limited to the amount you paid to Quantify for the Service during the three months immediately preceding the event giving rise to the claim.",
      "This limitation does not apply to liabilities that cannot lawfully be limited.",
    ],
  },
  {
    number: "26.",
    title: "Indemnification",
    paragraphs: [
      "To the extent permitted by applicable law, you agree to indemnify and hold harmless Quantify and its directors, officers, employees, and contractors from claims, losses, liabilities, damages, and reasonable expenses arising from:",
    ],
    items: [
      "your material breach of these Terms;",
      "your unlawful use of the Service;",
      "your violation of another person's rights; or",
      "fraudulent or intentionally wrongful conduct.",
    ],
  },
  {
    number: "27.",
    title: "No Waiver",
    paragraphs: [
      "If Quantify does not immediately enforce a provision of these Terms, that does not mean Quantify has waived its right to enforce that provision later.",
    ],
  },
  {
    number: "28.",
    title: "Severability",
    paragraphs: [
      "If any provision of these Terms is determined to be invalid or unenforceable, that provision will be modified or removed to the minimum extent necessary, and the remaining provisions will continue in effect.",
    ],
  },
  {
    number: "29.",
    title: "Changes to These Terms",
    paragraphs: [
      "We may update these Terms from time to time.",
      "When we make material changes, we may provide notice through:",
    ],
    items: [
      "the Quantify website;",
      "WhatsApp;",
      "email;",
      "the Service; or",
      "another reasonable method.",
    ],
    trailingParagraphs: [
      "The updated Terms will become effective on the date stated in the updated Terms.",
      "Your continued use of Quantify after the effective date constitutes acceptance of the updated Terms, to the extent permitted by applicable law.",
    ],
  },
  {
    number: "30.",
    title: "Governing Law",
    paragraphs: [
      "These Terms will be governed by the laws of India.",
      "Subject to applicable consumer-protection and other mandatory laws, disputes arising from these Terms or the Service will be subject to the courts having jurisdiction in Bengaluru, Karnataka, India.",
    ],
  },
  {
    number: "31.",
    title: "Consumer Rights",
    paragraphs: [
      "Nothing in these Terms is intended to remove, restrict, or waive any statutory rights or remedies available to consumers under applicable Indian law.",
      "Quantify will comply with applicable consumer-protection requirements.",
    ],
  },
  {
    number: "32.",
    title: "Grievances and Customer Support",
    paragraphs: [
      "For questions, complaints, technical issues, account issues, or other concerns, contact:",
      "We aim to acknowledge complaints and support requests within a reasonable period.",
      "Where applicable, Quantify will maintain grievance-handling mechanisms required by applicable law.",
    ],
  },
  {
    number: "33.",
    title: "Electronic Acceptance",
    paragraphs: [
      "By clicking an acceptance button, checking a box, registering for Quantify, purchasing a subscription, or otherwise accessing or using the Service after being presented with these Terms, you acknowledge that you have read, understood, and agreed to these Terms.",
      "Electronic acceptance will have the same effect as a written acceptance to the extent permitted by applicable law.",
    ],
  },
  {
    number: "34.",
    title: "Entire Agreement",
    paragraphs: [
      "These Terms, together with the Privacy Policy, applicable Refund Policy, and any additional terms presented to you for particular features, constitute the agreement between you and Quantify regarding the Service.",
      "If there is a conflict between these Terms and mandatory applicable law, the applicable law will prevail.",
    ],
  },
  {
    number: "35.",
    title: "Contact",
    paragraphs: [
      "Quantify",
    ],
  },
];

const BulletList = ({ items, testId }) => (
  <ul className="space-y-3 text-sm text-[#171717]/80 md:text-base" data-testid={testId}>
    {items.map((item) => (
      <li key={item} className="flex gap-3 leading-relaxed">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#176B4D]" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const SectionContent = ({ section }) => (
  <div className="space-y-4">
    {section.paragraphs?.map((paragraph) => (
      <p key={paragraph} className="text-sm leading-relaxed text-[#171717]/80 md:text-base">
        {paragraph}
      </p>
    ))}

    {section.emphasis ? (
      <div
        className="inline-flex w-fit rounded-full border border-[#176B4D]/20 bg-[#B8D99A]/22 px-4 py-2 font-heading text-lg font-semibold text-[#176B4D]"
        data-testid="terms-of-service-price-pill"
      >
        {section.emphasis}
      </div>
    ) : null}

    {section.items?.length ? (
      <BulletList items={section.items} testId={`terms-of-service-section-list-${section.number.replace(/\./g, "")}`} />
    ) : null}

    {section.trailingParagraphs?.map((paragraph) => (
      <p key={paragraph} className="text-sm leading-relaxed text-[#171717]/80 md:text-base">
        {paragraph}
      </p>
    ))}

    {section.trailingItems?.length ? (
      <BulletList
        items={section.trailingItems}
        testId={`terms-of-service-section-trailing-list-${section.number.replace(/\./g, "")}`}
      />
    ) : null}

    {section.closingParagraphs?.map((paragraph) => (
      <p key={paragraph} className="text-sm leading-relaxed text-[#171717]/80 md:text-base">
        {paragraph}
      </p>
    ))}

    {section.subsections?.map((subsection) => (
      <div key={subsection.heading} className="rounded-[1.25rem] border border-[#171717]/8 bg-white/65 p-5 md:p-6">
        <h3 className="font-heading text-xl font-semibold tracking-tight text-[#171717] md:text-[1.6rem]">
          {subsection.heading}
        </h3>
        <div className="mt-4 space-y-4">
          {subsection.paragraphs?.map((paragraph) => (
            <p key={paragraph} className="text-sm leading-relaxed text-[#171717]/80 md:text-base">
              {paragraph}
            </p>
          ))}
          {subsection.items?.length ? <BulletList items={subsection.items} testId={`terms-subsection-list-${subsection.heading.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`} /> : null}
          {subsection.trailingParagraphs?.map((paragraph) => (
            <p key={paragraph} className="text-sm leading-relaxed text-[#171717]/80 md:text-base">
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const ContactCard = ({ prefix }) => (
  <div className="rounded-[1.2rem] border border-[#176B4D]/15 bg-[#B8D99A]/18 p-4 text-sm text-[#171717]/82 md:text-base" data-testid={`${prefix}-contact-card`}>
    <p>Quantify</p>
    <p>
      Email:{" "}
      <a
        href="mailto:support@quantifyyourhealth.com"
        className="font-medium text-[#176B4D] underline decoration-[#176B4D]/35 underline-offset-4"
        data-testid={`${prefix}-contact-email-link`}
      >
        support@quantifyyourhealth.com
      </a>
    </p>
    <p>
      Support Phone:{" "}
      <a
        href="tel:+9180767704008"
        className="font-medium text-[#176B4D] underline decoration-[#176B4D]/35 underline-offset-4"
        data-testid={`${prefix}-contact-phone-link`}
      >
        +91 80767704008
      </a>
    </p>
    <p>
      Website:{" "}
      <a
        href="https://www.quantifyyourhealth.com"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-[#176B4D] underline decoration-[#176B4D]/35 underline-offset-4"
        data-testid={`${prefix}-contact-website-link`}
      >
        www.quantifyyourhealth.com
      </a>
    </p>
  </div>
);

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    trackEvent("terms_of_service_page_view");
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F8F7F3] text-[#171717]" data-testid="terms-of-service-page">
      <main className="relative isolate px-6 pb-20 pt-8 md:px-12 md:pb-24 md:pt-12">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[420px] bg-[radial-gradient(circle_at_top_left,_rgba(184,217,154,0.5),_transparent_45%),radial-gradient(circle_at_top_right,_rgba(23,107,77,0.12),_transparent_30%)]" />

        <div className="mx-auto flex w-full max-w-[980px] flex-col gap-8 md:gap-10">
          <Link
            to="/"
            data-testid="terms-of-service-back-home-link"
            className="inline-flex w-fit items-center gap-2 rounded-full border border-[#171717]/12 bg-white/70 px-4 py-2 text-sm font-medium text-[#171717] transition-[border-color,background-color,transform] duration-300 ease-out hover:border-[#171717]/25 hover:bg-white hover:-translate-y-0.5"
          >
            <span aria-hidden="true">←</span>
            Back to home
          </Link>

          <section
            className="rounded-[2rem] border border-[#171717]/10 bg-white/80 px-6 py-8 shadow-[0_20px_80px_rgba(23,23,23,0.06)] md:px-10 md:py-12"
            data-testid="terms-of-service-hero"
          >
            <div className="mb-5 flex items-center gap-3 text-[#171717]/45" data-testid="terms-of-service-label">
              <span className="h-px w-8 bg-[#171717]/20" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em]">QUANTIFY — Terms of Service</span>
            </div>

            <h1 className="font-heading text-4xl font-semibold tracking-tight text-[#171717] sm:text-5xl lg:text-6xl" data-testid="terms-of-service-title">
              QUANTIFY — TERMS OF SERVICE
            </h1>

            <p className="mt-4 text-sm text-[#171717]/55 md:text-base" data-testid="terms-of-service-last-updated">
              Last Updated: September 2026
            </p>

            <p className="mt-8 max-w-[760px] text-sm leading-relaxed text-[#171717]/78 md:text-base" data-testid="terms-of-service-intro">
              These Terms of Service ("Terms") govern your access to and use of Quantify, including its WhatsApp-based nutrition and fitness tracking service, website, software, artificial-intelligence features, communications, and related services (collectively, the "Service").
            </p>
            <p className="mt-4 max-w-[760px] text-sm leading-relaxed text-[#171717]/78 md:text-base">
              By registering for, accessing, or using Quantify, you agree to be bound by these Terms and our Privacy Policy.
            </p>
            <p className="mt-4 max-w-[760px] text-sm leading-relaxed text-[#171717]/78 md:text-base">
              If you do not agree with these Terms, you must not use the Service.
            </p>
          </section>

          <section className="grid gap-5" data-testid="terms-of-service-sections">
            {sections.map((section) => (
              <article
                key={section.number}
                className="rounded-[1.75rem] border border-[#171717]/9 bg-[#F8F7F3]/80 px-6 py-6 shadow-[0_12px_40px_rgba(23,23,23,0.04)] md:px-8 md:py-7"
                data-testid={`terms-of-service-section-${section.number.replace(/\./g, "")}`}
              >
                <div className="flex flex-col gap-4">
                  <h2 className="font-heading text-2xl font-semibold tracking-tight text-[#171717] md:text-[2rem]" data-testid={`terms-of-service-section-title-${section.number.replace(/\./g, "")}`}>
                    {section.number} {section.title}
                  </h2>

                  <SectionContent section={section} />

                  {section.number === "32." ? <ContactCard prefix="terms-support" /> : null}
                  {section.number === "35." ? <ContactCard prefix="terms-final" /> : null}
                </div>
              </article>
            ))}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}